import { useState, useEffect, useMemo } from 'react';
import api from '../../services/api';
import { toast } from 'sonner';
import {
  Clock, User, Filter, RefreshCw, Shield, DollarSign, CheckCircle2, XCircle,
  Settings, UserX, KeyRound, ArrowDownCircle, ArrowUpCircle, LogIn, Ban, Wallet,
  Calendar, FileText, AlertTriangle,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';

/*
 * Staff Activity — compact audit feed for tenant + master admins.
 *
 * Revamp (Phase 21k):
 *   • Clickable KPI strip at the top — each tile filters the feed to that
 *     category (deposits approved, deposits rejected, withdrawals, wallet
 *     adjustments, logins, permission denials, settings changes, all).
 *   • Details are rendered as a readable 2-column diff instead of raw JSON:
 *     left=before, right=after, plus a meta row for context fields.
 */

const fmtTime = (ts) => {
  if (!ts) return '';
  try {
    return new Date(ts).toLocaleString([], {
      hour: '2-digit', minute: '2-digit',
      day: '2-digit', month: 'short', year: 'numeric',
    });
  } catch { return String(ts); }
};

const fmtDateShort = (ts) => {
  if (!ts) return '';
  try { return new Date(ts).toLocaleDateString(); } catch { return ts; }
};

const humanize = (s) =>
  String(s || '').replace(/[_.]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

// Maps raw action codes to a friendly label + icon + tone.
const ACTION_META = {
  login: { label: 'Signed in', icon: LogIn, tone: 'blue' },
  logout: { label: 'Signed out', icon: LogIn, tone: 'gray' },
  wallet_adjustment: { label: 'Wallet adjustment', icon: Wallet, tone: 'amber' },
  'wallet.debit_cash': { label: 'Wallet debited', icon: Wallet, tone: 'red' },
  'wallet.credit_cash': { label: 'Wallet credited', icon: Wallet, tone: 'green' },
  deposit_approve: { label: 'Deposit approved', icon: CheckCircle2, tone: 'green' },
  deposit_reject: { label: 'Deposit rejected', icon: XCircle, tone: 'red' },
  withdrawal_approve: { label: 'Withdrawal approved', icon: CheckCircle2, tone: 'green' },
  withdrawal_reject: { label: 'Withdrawal rejected', icon: XCircle, tone: 'red' },
  'order.create': { label: 'Deposit created', icon: ArrowDownCircle, tone: 'blue' },
  'order.status_change': { label: 'Order status changed', icon: ArrowDownCircle, tone: 'blue' },
  'payment.attach': { label: 'Payment attached', icon: DollarSign, tone: 'green' },
  'gateway.link_generated': { label: 'Payment link generated', icon: DollarSign, tone: 'blue' },
  'gateway.deposit_settled': { label: 'Deposit auto-settled', icon: CheckCircle2, tone: 'green' },
  user_update: { label: 'User updated', icon: Settings, tone: 'gray' },
  disable_user: { label: 'User disabled', icon: UserX, tone: 'red' },
  enable_user: { label: 'User enabled', icon: CheckCircle2, tone: 'green' },
  reset_password: { label: 'Password reset', icon: KeyRound, tone: 'amber' },
  change_role: { label: 'Role changed', icon: Shield, tone: 'amber' },
  permission_grant: { label: 'Permission granted', icon: Shield, tone: 'green' },
  permission_deny: { label: 'Permission denied', icon: Shield, tone: 'red' },
  permission_denied: { label: 'Permission denied', icon: Ban, tone: 'red' },
  payment_method_create: { label: 'Payment method created', icon: Settings, tone: 'green' },
  payment_method_update: { label: 'Payment method updated', icon: Settings, tone: 'gray' },
  payment_method_delete: { label: 'Payment method deleted', icon: Settings, tone: 'red' },
  settings_change: { label: 'Settings changed', icon: Settings, tone: 'amber' },
};

const metaFor = (action) => ACTION_META[action] || { label: humanize(action), icon: Settings, tone: 'gray' };

const TONES = {
  green:  { bg: 'bg-green-500/15', fg: 'text-green-400', br: 'border-green-500/25' },
  red:    { bg: 'bg-red-500/15',   fg: 'text-red-400',   br: 'border-red-500/25' },
  amber:  { bg: 'bg-amber-500/15', fg: 'text-amber-300', br: 'border-amber-500/25' },
  blue:   { bg: 'bg-blue-500/15',  fg: 'text-blue-400',  br: 'border-blue-500/25' },
  gray:   { bg: 'bg-white/5',      fg: 'text-gray-300',  br: 'border-white/10' },
};

// Classification helpers — compute KPI counts from the loaded logs.
const isDepositApproved = (l) => (
  l.action === 'deposit_approve' ||
  (l.action === 'order.status_change' &&
   String(l.details?.after?.status || '').toLowerCase().match(/^(approved|completed)$/))
);
const isDepositRejected = (l) => (
  l.action === 'deposit_reject' ||
  (l.action === 'order.status_change' &&
   String(l.details?.after?.status || '').toLowerCase().match(/^(rejected|cancelled|failed)$/))
);
const isWithdrawApproved = (l) => (
  l.action === 'withdrawal_approve' ||
  (l.target_type === 'payout' && String(l.details?.after?.status || '').toLowerCase().match(/^(approved|paid|completed)$/))
);
const isWithdrawRejected = (l) => (
  l.action === 'withdrawal_reject' ||
  (l.target_type === 'payout' && String(l.details?.after?.status || '').toLowerCase().match(/^(rejected|cancelled)$/))
);
const isWalletAdjust = (l) => l.action === 'wallet_adjustment' || l.action?.startsWith('wallet.');
const isLoginEvent = (l) => l.action === 'login' || l.action === 'logout';
const isPermDenied = (l) => l.action === 'permission_denied' || l.action === 'permission_deny';
const isSettingsChange = (l) => (
  l.action === 'settings_change' ||
  l.action?.startsWith('payment_method_') ||
  l.action === 'user_update' ||
  l.action === 'change_role'
);

// Map each KPI id to its row filter function.
const KPI_FILTERS = {
  all: () => true,
  deposits_approved: isDepositApproved,
  deposits_rejected: isDepositRejected,
  withdrawals_approved: isWithdrawApproved,
  withdrawals_rejected: isWithdrawRejected,
  wallet_adjustments: isWalletAdjust,
  logins: isLoginEvent,
  perm_denied: isPermDenied,
  settings: isSettingsChange,
};

export default function StaffActivityPage() {
  const [logs, setLogs] = useState([]);
  const [actionTypes, setActionTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeKpi, setActiveKpi] = useState('all');
  const [filters, setFilters] = useState({ action: '', admin_id: '', search: '' });
  const [expanded, setExpanded] = useState({});

  const fetchAll = async () => {
    setRefreshing(true);
    try {
      const params = { limit: 300 };
      if (filters.action) params.action = filters.action;
      if (filters.admin_id) params.admin_id = filters.admin_id;
      const res = await api.get('/api/admin/audit', { params });
      const items = res.data?.data?.logs || res.data?.logs || [];
      setLogs(items);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load audit log');
    } finally {
      setLoading(false); setRefreshing(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const at = await api.get('/api/admin/audit/action-types');
        setActionTypes(at.data?.data?.items || []);
      } catch {}
    })();
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const t = setTimeout(fetchAll, 150);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.action, filters.admin_id]);

  // KPI counts across the current logs load (not just visible filter).
  const kpis = useMemo(() => ({
    all: logs.length,
    deposits_approved: logs.filter(isDepositApproved).length,
    deposits_rejected: logs.filter(isDepositRejected).length,
    withdrawals_approved: logs.filter(isWithdrawApproved).length,
    withdrawals_rejected: logs.filter(isWithdrawRejected).length,
    wallet_adjustments: logs.filter(isWalletAdjust).length,
    logins: logs.filter(isLoginEvent).length,
    perm_denied: logs.filter(isPermDenied).length,
    settings: logs.filter(isSettingsChange).length,
  }), [logs]);

  const filteredLogs = useMemo(() => {
    const byKpi = logs.filter(KPI_FILTERS[activeKpi] || KPI_FILTERS.all);
    const q = filters.search.trim().toLowerCase();
    if (!q) return byKpi;
    return byKpi.filter((l) =>
      (l.admin_username || '').toLowerCase().includes(q) ||
      (l.action || '').toLowerCase().includes(q) ||
      (l.target_type || '').toLowerCase().includes(q) ||
      (l.target_id || '').toLowerCase().includes(q) ||
      JSON.stringify(l.details || {}).toLowerCase().includes(q)
    );
  }, [logs, activeKpi, filters.search]);

  const uniqueAdmins = useMemo(() => {
    const map = new Map();
    logs.forEach((l) => { if (l.admin_id) map.set(l.admin_id, l.admin_username || l.admin_id); });
    return Array.from(map.entries());
  }, [logs]);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;

  return (
    <div className="space-y-5 animate-fade-in-up" data-testid="staff-activity-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <Shield className="w-6 h-6 text-[#D4AF37]" /> Staff Activity
          </h2>
          <p className="text-xs text-gray-400 mt-0.5">
            Full audit trail for every admin action across the platform —
            approvals, rejections, wallet changes, logins. Click a KPI to filter.
          </p>
        </div>
        <Button
          onClick={fetchAll}
          variant="outline"
          className="border-white/10 rounded-full"
          disabled={refreshing}
          data-testid="audit-refresh"
        >
          <RefreshCw className={`w-4 h-4 mr-1.5 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Clickable KPI strip */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5" data-testid="staff-kpi-strip">
        <KpiTile id="all" activeKpi={activeKpi} setActiveKpi={setActiveKpi}
          label="All Events" count={kpis.all} icon={FileText} color="#D4AF37" />
        <KpiTile id="deposits_approved" activeKpi={activeKpi} setActiveKpi={setActiveKpi}
          label="Deposits Approved" count={kpis.deposits_approved} icon={ArrowDownCircle} color="#22c55e" />
        <KpiTile id="deposits_rejected" activeKpi={activeKpi} setActiveKpi={setActiveKpi}
          label="Deposits Rejected" count={kpis.deposits_rejected} icon={XCircle} color="#ef4444" />
        <KpiTile id="withdrawals_approved" activeKpi={activeKpi} setActiveKpi={setActiveKpi}
          label="Withdrawals OK" count={kpis.withdrawals_approved} icon={ArrowUpCircle} color="#22c55e" />
        <KpiTile id="withdrawals_rejected" activeKpi={activeKpi} setActiveKpi={setActiveKpi}
          label="Withdrawals Rejected" count={kpis.withdrawals_rejected} icon={XCircle} color="#ef4444" />
        <KpiTile id="wallet_adjustments" activeKpi={activeKpi} setActiveKpi={setActiveKpi}
          label="Wallet Adjustments" count={kpis.wallet_adjustments} icon={Wallet} color="#f59e0b" />
        <KpiTile id="logins" activeKpi={activeKpi} setActiveKpi={setActiveKpi}
          label="Login Events" count={kpis.logins} icon={LogIn} color="#3b82f6" />
        <KpiTile id="perm_denied" activeKpi={activeKpi} setActiveKpi={setActiveKpi}
          label="Access Denied" count={kpis.perm_denied} icon={AlertTriangle} color="#ef4444" />
        <KpiTile id="settings" activeKpi={activeKpi} setActiveKpi={setActiveKpi}
          label="Settings Changes" count={kpis.settings} icon={Settings} color="#8b5cf6" />
        <div className="rounded-xl border border-dashed border-white/10 bg-white/[0.02] flex items-center justify-center text-[10px] text-gray-500 px-3 py-2.5">
          Showing {filteredLogs.length} of {logs.length}
        </div>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-2.5">
        <div className="relative md:col-span-2">
          <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <Input
            value={filters.search}
            onChange={(e) => setFilters((p) => ({ ...p, search: e.target.value }))}
            placeholder="Search by admin, action, target, details…"
            className="pl-9 bg-[#0B0E14] border-white/10 rounded-full"
            data-testid="audit-search"
          />
        </div>
        <select
          value={filters.action}
          onChange={(e) => setFilters((p) => ({ ...p, action: e.target.value }))}
          className="bg-[#0B0E14] border border-white/10 rounded-full px-3 py-2 text-sm text-white"
          data-testid="audit-action-filter"
        >
          <option value="">All actions</option>
          {actionTypes.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
        <select
          value={filters.admin_id}
          onChange={(e) => setFilters((p) => ({ ...p, admin_id: e.target.value }))}
          className="bg-[#0B0E14] border border-white/10 rounded-full px-3 py-2 text-sm text-white"
          data-testid="audit-admin-filter"
        >
          <option value="">All staff</option>
          {uniqueAdmins.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
        </select>
      </div>

      {/* Activity list */}
      {filteredLogs.length === 0 ? (
        <div className="text-center py-12 glass-card rounded-xl border border-white/10">
          <Clock className="w-10 h-10 mx-auto mb-3 text-gray-600" />
          <p className="text-gray-400 text-sm">No audit events match your filters.</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="audit-rows">
          {filteredLogs.map((log, idx) => {
            const m = metaFor(log.action);
            const tone = TONES[m.tone] || TONES.gray;
            const Icon = m.icon;
            const key = log.id || `${log.created_at}-${idx}`;
            const isOpen = !!expanded[key];
            const hasDetails = log.details && Object.keys(log.details).length > 0;
            // Compute a concise summary line specific to the action type.
            const summary = buildSummary(log);

            return (
              <div
                key={key}
                className="glass-card rounded-xl border border-white/10 overflow-hidden"
                data-testid={`audit-row-${idx}`}
              >
                <button
                  onClick={() => hasDetails && setExpanded((p) => ({ ...p, [key]: !isOpen }))}
                  className={`w-full text-left p-3 flex items-start gap-3 ${hasDetails ? 'hover:bg-white/5' : ''}`}
                >
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center border flex-shrink-0 ${tone.bg} ${tone.fg} ${tone.br}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-semibold text-white">{m.label}</p>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${tone.bg} ${tone.fg} ${tone.br} border`}>
                        {log.action}
                      </span>
                      {log.admin_role && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 text-gray-400 border border-white/5">
                          {log.admin_role}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-400 mt-0.5">
                      by <span className="text-gray-200 font-medium">{log.admin_username || 'System'}</span>
                      {log.target_type && <> on <span className="text-gray-200">{log.target_type}</span></>}
                      {log.target_id && (
                        <> <code className="text-[10px] text-gray-500">({String(log.target_id).slice(0, 8)}…)</code></>
                      )}
                    </p>
                    {summary && (
                      <p className="text-[12px] text-gray-300 mt-1 leading-snug">{summary}</p>
                    )}
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-[11px] text-gray-400 flex items-center gap-1 justify-end">
                      <Calendar className="w-3 h-3" /> {fmtDateShort(log.created_at)}
                    </p>
                    <p className="text-[10px] text-gray-500 mt-0.5">{fmtTime(log.created_at).split(', ').pop()}</p>
                    {hasDetails && (
                      <p className="text-[10px] text-[#D4AF37] mt-1">
                        {isOpen ? 'Hide details ▴' : 'Show details ▾'}
                      </p>
                    )}
                  </div>
                </button>
                {isOpen && hasDetails && (
                  <div className="px-3 pb-3 border-t border-white/5 bg-black/20" data-testid={`audit-details-${idx}`}>
                    <PrettyDetails details={log.details} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function KpiTile({ id, activeKpi, setActiveKpi, label, count, icon: Icon, color }) {
  const active = activeKpi === id;
  return (
    <button
      onClick={() => setActiveKpi(id)}
      className={`rounded-xl p-3 border flex items-center gap-3 transition text-left ${
        active
          ? 'bg-white/[0.05] border-white/30 shadow-inner'
          : 'bg-[#131822] border-white/10 hover:border-white/20'
      }`}
      data-testid={`kpi-${id}`}
    >
      <div
        className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
        style={{ background: `${color}22` }}
      >
        <Icon className="w-5 h-5" style={{ color }} />
      </div>
      <div className="min-w-0">
        <p className="text-[10px] uppercase tracking-wider text-gray-500">{label}</p>
        <p className="text-lg font-bold tabular-nums text-white">{Number(count || 0).toLocaleString()}</p>
      </div>
    </button>
  );
}

// Turn the raw log record into a short human sentence for the row header.
function buildSummary(log) {
  const d = log.details || {};
  if (!d || typeof d !== 'object') return null;
  const a = d.after || {};
  const b = d.before || {};
  const meta = d.meta || {};

  // Order status change
  if (log.action === 'order.status_change' && (b.status || a.status)) {
    const amt = meta.amount ?? d.amount;
    const parts = [`status: ${b.status || '—'} → ${a.status || '—'}`];
    if (amt !== undefined) parts.push(`amount $${Number(amt).toFixed(2)}`);
    if (meta.payment_mode) parts.push(meta.payment_mode);
    return parts.join(' · ');
  }
  // Wallet adjustment — simple shape
  if (log.action === 'wallet_adjustment') {
    return `${d.type === 'add' ? '+' : d.type === 'subtract' ? '−' : ''}${d.amount || 0} · ${d.reason || 'no reason'}`;
  }
  // Wallet debit/credit (before/after on balance)
  if (log.action?.startsWith('wallet.')) {
    const from = b.balance ?? b.cash_balance;
    const to = a.balance ?? a.cash_balance;
    if (from != null || to != null) return `balance ${from ?? '—'} → ${to ?? '—'}`;
  }
  // Permission denied
  if (log.action === 'permission_denied' || log.action === 'permission_deny') {
    return `required: ${d.permission_required || '—'}`;
  }
  // Generic: show first key/value pair
  const entries = Object.entries(d).filter(([k]) => !['before', 'after', 'meta', 'timestamp'].includes(k));
  if (entries.length) {
    const [k, v] = entries[0];
    return `${k}: ${String(v).slice(0, 120)}`;
  }
  return null;
}

// Readable details panel — shows before/after side-by-side + meta row.
function PrettyDetails({ details }) {
  const before = details.before || {};
  const after = details.after || {};
  const meta = details.meta || {};
  const hasDiff = Object.keys(before).length > 0 || Object.keys(after).length > 0;

  // Render any non-before/after/meta/timestamp fields as a "fields" block too
  // (e.g. wallet_adjustment uses top-level amount/type/reason).
  const top = Object.entries(details).filter(
    ([k]) => !['before', 'after', 'meta', 'timestamp'].includes(k)
  );

  return (
    <div className="pt-3 space-y-3">
      {top.length > 0 && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
          {top.map(([k, v]) => (
            <Field key={k} label={k} value={v} />
          ))}
        </div>
      )}

      {hasDiff && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <DiffBlock title="Before" color="#6b7280" data={before} />
          <DiffBlock title="After" color="#22c55e" data={after} />
        </div>
      )}

      {Object.keys(meta).length > 0 && (
        <div>
          <p className="text-[10px] uppercase tracking-wider text-gray-500 mb-1.5">Meta</p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
            {Object.entries(meta).map(([k, v]) => (
              <Field key={k} label={k} value={v} />
            ))}
          </div>
        </div>
      )}

      {details.timestamp && (
        <p className="text-[10px] text-gray-600 flex items-center gap-1">
          <Clock className="w-3 h-3" /> recorded {fmtTime(details.timestamp)}
        </p>
      )}
    </div>
  );
}

function DiffBlock({ title, color, data }) {
  const entries = Object.entries(data || {});
  return (
    <div className="rounded-lg bg-black/30 border border-white/5 p-2.5">
      <p className="text-[10px] uppercase tracking-wider mb-1.5" style={{ color }}>{title}</p>
      {entries.length === 0 ? (
        <p className="text-[11px] text-gray-500 italic">empty</p>
      ) : (
        <div className="space-y-1">
          {entries.map(([k, v]) => (
            <div key={k} className="flex items-start gap-2 text-[11px]">
              <span className="text-gray-500 w-28 flex-shrink-0 truncate" title={k}>{k}</span>
              <span className="text-gray-200 font-mono break-all flex-1" title={String(v)}>
                {renderValue(v)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div className="rounded-lg bg-white/[0.02] border border-white/5 p-2">
      <p className="text-[9px] uppercase tracking-wider text-gray-500 truncate">{label}</p>
      <p className="text-[12px] text-gray-200 font-mono break-all truncate" title={String(value)}>
        {renderValue(value)}
      </p>
    </div>
  );
}

function renderValue(v) {
  if (v === null || v === undefined) return <span className="text-gray-600">—</span>;
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}
